// Manuais: PDFs, imagens e vídeos para mandar na conversa (ícone 📄 da barra de mensagem).
// Cadastro separado das respostas rápidas.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  IconButton, InputAdornment, Paper, TextField, Typography, makeStyles,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import SearchIcon from "@material-ui/icons/Search";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import Autocomplete from "@material-ui/lab/Autocomplete";
import DescriptionIcon from "@material-ui/icons/Description";
import PublishIcon from "@material-ui/icons/Publish";
import GetAppIcon from "@material-ui/icons/GetApp";
import Tooltip from "@material-ui/core/Tooltip";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import suporteApi, { mensagemErro } from "../api";
import { usePermissao } from "../permissoes";
import Galeria from "../Galeria";
import EditorPilula from "./EditorPilula";

export const urlManual = (a) => `${String(suporteApi.defaults.baseURL || "").replace(/\/$/, "")}${a.url}`;

const tamanho = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((n || 0) / 1024))} KB`);

// tags do manual: clientes e marcas (DPSP, Intelbras…), para filtrar na tela e na busca da conversa
export const CampoTags = ({ valor, opcoes, onChange }) => (
  <Autocomplete multiple freeSolo size="small" options={opcoes.filter((o) => !valor.some((v) => v.toLowerCase() === o.toLowerCase()))}
    value={valor} onChange={(_, v) => onChange(v.map((x) => String(x).trim()).filter(Boolean))}
    renderTags={(v, getProps) => v.map((t, i) => <Chip size="small" color="primary" label={t} {...getProps({ index: i })} />)}
    renderInput={(p) => (
      <TextField {...p} label="Tags (clientes, marcas…)" variant="outlined" margin="dense"
        placeholder={valor.length ? "" : "Ex.: DPSP, Intelbras"} helperText="Digite e tecle Enter para criar uma tag nova" />
    )} />
);

export const listarTags = (lista) => Array.from(new Map((lista || []).flatMap((m) => m.tags || [])
  .map((t) => [t.toLowerCase(), t])).values()).sort((a, b) => a.localeCompare(b, "pt-BR"));

const useStyles = makeStyles((theme) => ({
  grade: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12, padding: 8,
    overflowY: "auto", ...theme.scrollbarStyles },
  cartao: { border: `1px solid ${theme.palette.divider}`, borderRadius: 12, padding: 14, background: theme.palette.background.paper,
    display: "flex", flexDirection: "column", gap: 8 },
  topo: { display: "flex", alignItems: "flex-start", gap: 6 },
  fraco: { color: theme.palette.text.secondary, fontSize: 12.5 },
  filtros: { padding: "8px 12px", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 8 },
}));

const Editor = ({ manual, filas, tags: todasTags, onClose, onSalvo }) => {
  const [f, setF] = useState(null);
  const [novos, setNovos] = useState([]);
  const [salvando, setSalvando] = useState(false);
  useEffect(() => {
    if (manual) {
      setF({ titulo: manual.titulo || "", mensagem: manual.mensagem || "", filas: manual.filas || [], tags: manual.tags || [],
        manter: (manual.arquivos || []).map((a) => a.arquivo) });
      setNovos([]);
    }
  }, [manual]);
  if (!manual || !f) return null;
  const salvar = async () => {
    const corpo = new FormData();
    corpo.append("titulo", f.titulo);
    corpo.append("mensagem", f.mensagem);
    corpo.append("filas", JSON.stringify(f.filas));
    corpo.append("manter", JSON.stringify(f.manter));
    corpo.append("tags", JSON.stringify(f.tags));
    novos.forEach((arq) => corpo.append("arquivos", arq));
    setSalvando(true);
    try {
      const { data } = manual.id
        ? await suporteApi.put(`/api/manuais/${manual.id}`, corpo)
        : await suporteApi.post("/api/manuais", corpo);
      toast.success("Manual salvo");
      onSalvo(data);
      onClose();
    } catch (err) {
      toast.error(mensagemErro(err));
    }
    setSalvando(false);
  };
  const atuais = (manual.arquivos || []).filter((a) => f.manter.includes(a.arquivo));
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{manual.id ? "Editar manual" : "Novo manual"}</DialogTitle>
      <DialogContent>
        <TextField label="Título" required fullWidth size="small" variant="outlined" margin="dense"
          placeholder="Ex.: Manual MIG800" value={f.titulo} onChange={(e) => setF({ ...f, titulo: e.target.value })} />
        <TextField label="Mensagem que vai junto (opcional)" fullWidth size="small" variant="outlined" margin="dense"
          multiline rows={3} value={f.mensagem} onChange={(e) => setF({ ...f, mensagem: e.target.value })}
          helperText="Aceita as variáveis {{nome}}, {{os}}, {{conta}}… como nas respostas rápidas" />
        <CampoTags valor={f.tags} opcoes={todasTags} onChange={(tags) => setF({ ...f, tags })} />
        <Typography variant="subtitle2" style={{ marginTop: 10 }}>Arquivos</Typography>
        <Typography variant="caption" color="textSecondary">PDF, imagem, vídeo ou documento · até 50 MB cada</Typography>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "6px 0" }}>
          {atuais.map((a) => (
            <Chip key={a.arquivo} label={`${a.nome} · ${tamanho(a.tamanho)}`}
              onDelete={() => setF({ ...f, manter: f.manter.filter((x) => x !== a.arquivo) })} />
          ))}
          {novos.map((arq, i) => (
            <Chip key={`n${i}`} color="primary" variant="outlined" label={`${arq.name} · ${tamanho(arq.size)}`}
              onDelete={() => setNovos(novos.filter((_, j) => j !== i))} />
          ))}
        </div>
        <Button size="small" variant="outlined" color="primary" component="label" startIcon={<AttachFileIcon />}>
          Anexar arquivo
          <input hidden multiple type="file" onChange={(e) => { setNovos([...novos, ...Array.from(e.target.files || [])]); e.target.value = ""; }} />
        </Button>
        <Typography variant="subtitle2" style={{ marginTop: 14 }}>Mostrar só nas filas</Typography>
        {filas.map((q) => (
          <FormControlLabel key={q.id} label={q.nome}
            control={<Checkbox size="small" color="primary" checked={f.filas.includes(q.id)}
              onChange={(e) => setF({ ...f, filas: e.target.checked ? [...f.filas, q.id] : f.filas.filter((x) => x !== q.id) })} />} />
        ))}
        <Typography variant="caption" color="textSecondary" component="div">Sem fila marcada, o manual aparece em todas.</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button color="primary" variant="contained" disabled={salvando || !f.titulo.trim() || !(atuais.length + novos.length)}
          onClick={salvar}>Salvar</Button>
      </DialogActions>
    </Dialog>
  );
};

const Manuais = () => {
  const c = useStyles();
  const podeGerenciar = usePermissao("manuais.gerenciar");
  const [lista, setLista] = useState(null);
  const [filas, setFilas] = useState([]);
  const [busca, setBusca] = useState("");
  const [filtroTags, setFiltroTags] = useState([]);
  const [editando, setEditando] = useState(null);
  const [pilula, setPilula] = useState(null); // {} = nova; manual = editar

  const carregar = useCallback(async () => {
    try {
      setLista((await suporteApi.get("/api/manuais")).data);
    } catch (err) {
      toast.error(mensagemErro(err));
    }
  }, []);
  useEffect(() => {
    carregar();
    suporteApi.get("/api/formularios/filas").then((r) => setFilas(r.data)).catch(() => {});
  }, [carregar]);

  // HTML salvo pelo Editor de Pílula Técnica: abre no editor e "Salvar na galeria" gera o PDF.
  // Se veio do "Baixar HTML" de um manual, pergunta se atualiza esse mesmo manual.
  const importar = async (e) => {
    const arq = e.target.files?.[0];
    e.target.value = "";
    if (!arq) return;
    const texto = await arq.text();
    if (!texto.includes('id="documento"')) {
      toast.error("Este HTML não é uma Pílula Técnica do editor");
      return;
    }
    const id = Number((texto.match(/<meta name="allarmi-manual" content="(\d+)"/) || [])[1]);
    const dono = id && (lista || []).find((m) => m.id === id);
    if (dono && window.confirm(`Este arquivo veio do manual "${dono.titulo}".\n\nOK = atualizar esse manual\nCancelar = criar um manual novo`)) {
      setPilula({ ...dono, htmlInicial: texto, arquivo: arq.name });
      return;
    }
    setPilula({ htmlInicial: texto, arquivo: arq.name });
  };

  // download para editar no computador (abre no navegador, "Salvar edição" baixa o HTML) e depois "Importar pílula"
  const baixar = (html, nome) => {
    const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = nome;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };
  const nomeArquivo = (t) => `${(t || "pilula").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^\w]+/g, "-")
    .replace(/^-|-$/g, "").toLowerCase()}.html`;
  const baixarModelo = async () => {
    try {
      const { data } = await suporteApi.get("/static/pilula-modelo.html", { responseType: "text", transformResponse: (r) => r });
      baixar(data, "pilula-tecnica-modelo.html");
    } catch (err) {
      toast.error(mensagemErro(err));
    }
  };
  const baixarFonte = async (m) => {
    try {
      const { data } = await suporteApi.get(`/api/manuais/${m.id}/fonte`, { responseType: "text", transformResponse: (r) => r });
      const marca = `<meta name="allarmi-manual" content="${m.id}">`;
      const html = data.replace(/<meta name="allarmi-manual"[^>]*>/g, "").replace(/<head([^>]*)>/i, `<head$1>${marca}`);
      baixar(html, nomeArquivo(m.titulo));
    } catch (err) {
      toast.error(mensagemErro(err));
    }
  };

  const excluir = async (m) => {
    if (!window.confirm(`Excluir o manual "${m.titulo}"? Os arquivos também serão apagados.`)) return;
    try {
      await suporteApi.delete(`/api/manuais/${m.id}`);
      toast.success("Manual excluído");
      carregar();
    } catch (err) {
      toast.error(mensagemErro(err));
    }
  };

  const tagsConhecidas = listarTags(lista);
  const t = busca.trim().toLowerCase();
  const temTag = (m, tag) => (m.tags || []).some((x) => x.toLowerCase() === tag.toLowerCase());
  const visiveis = (lista || []).filter((m) => (!t || `${m.titulo} ${m.mensagem || ""} ${(m.tags || []).join(" ")} ${(m.arquivos || []).map((a) => a.nome).join(" ")}`
    .toLowerCase().includes(t)) && filtroTags.every((tag) => temTag(m, tag)));
  const alternarTag = (tag) => setFiltroTags(filtroTags.includes(tag) ? filtroTags.filter((x) => x !== tag) : [...filtroTags, tag]);
  const nomeFila = (id) => filas.find((q) => q.id === id)?.nome || `fila ${id}`;

  return (
    <MainContainer>
      <MainHeader>
        <Title>{`Manuais (${visiveis.length})`}</Title>
        <MainHeaderButtonsWrapper>
          {podeGerenciar && (
            <Button variant="outlined" color="primary" startIcon={<DescriptionIcon />} onClick={() => setPilula({})}>
              Criar pílula técnica
            </Button>
          )}
          <Tooltip title="Baixa o modelo em branco para editar no computador (abre no navegador) e depois usar Importar pílula">
            <Button variant="outlined" color="primary" startIcon={<GetAppIcon />} onClick={baixarModelo}>Baixar modelo</Button>
          </Tooltip>
          {podeGerenciar && (
            <Button variant="outlined" color="primary" component="label" startIcon={<PublishIcon />}>
              Importar pílula
              <input hidden type="file" accept=".html,.htm,text/html" onChange={importar} />
            </Button>
          )}
          {podeGerenciar && (
            <Button variant="contained" color="primary" startIcon={<AddIcon />}
              onClick={() => setEditando({ titulo: "", mensagem: "", filas: [], tags: [], arquivos: [] })}>Novo manual</Button>
          )}
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper variant="outlined" className={c.filtros}>
        <TextField size="small" variant="outlined" value={busca} onChange={(e) => setBusca(e.target.value)} style={{ flex: "1 1 320px" }}
          placeholder="Buscar por título, texto, tag ou nome do arquivo"
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
        <Typography variant="caption" color="textSecondary">Na conversa, use o ícone 📄 ao lado do clipe para enviar.</Typography>
        {!!tagsConhecidas.length && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", width: "100%" }}>
            <LocalOfferIcon fontSize="small" color="action" />
            {tagsConhecidas.map((tag) => (
              <Chip key={tag} size="small" label={tag} clickable color={filtroTags.includes(tag) ? "primary" : "default"}
                variant={filtroTags.includes(tag) ? "default" : "outlined"} onClick={() => alternarTag(tag)} />
            ))}
            {!!filtroTags.length && <Button size="small" onClick={() => setFiltroTags([])}>Limpar filtro</Button>}
          </div>
        )}
      </Paper>
      {!lista ? <CircularProgress size={24} /> : (
        <div className={c.grade}>
          {!visiveis.length && <Typography color="textSecondary">Nenhum manual cadastrado.</Typography>}
          {visiveis.map((m) => (
            <div key={m.id} className={c.cartao}>
              <div className={c.topo}>
                <Typography variant="subtitle1" style={{ flex: 1 }}><b>{m.titulo}</b></Typography>
                {m.editavel && podeGerenciar && (
                  <Button size="small" color="primary" startIcon={<DescriptionIcon fontSize="small" />}
                    onClick={() => setPilula(m)}>Editar pílula</Button>
                )}
                {m.editavel && (
                  <Tooltip title="Baixar HTML para editar no computador">
                    <IconButton size="small" onClick={() => baixarFonte(m)}><GetAppIcon fontSize="small" /></IconButton>
                  </Tooltip>
                )}
                {podeGerenciar && <IconButton size="small" onClick={() => setEditando(m)}><EditIcon fontSize="small" /></IconButton>}
                {podeGerenciar && <IconButton size="small" onClick={() => excluir(m)}><DeleteOutlineIcon fontSize="small" /></IconButton>}
              </div>
              {!!(m.tags || []).length && (
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {m.tags.map((tag) => (
                    <Chip key={tag} size="small" color="primary" icon={<LocalOfferIcon style={{ fontSize: 14 }} />} label={tag}
                      clickable onClick={() => !filtroTags.some((x) => x.toLowerCase() === tag.toLowerCase()) && alternarTag(tag)} />
                  ))}
                </div>
              )}
              {m.mensagem && <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>{m.mensagem}</Typography>}
              <Galeria itens={(m.arquivos || []).map((a) => ({ url: urlManual(a), titulo: a.nome,
                capa: /\.pdf$/i.test(a.arquivo) ? `${urlManual(a)}/capa.png?t=${a.tamanho || 0}` : undefined }))} />
              <div className={c.fraco}>{(m.arquivos || []).map((a) => `${a.nome} (${tamanho(a.tamanho)})`).join(" · ")}</div>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                {(m.filas || []).length ? m.filas.map((id) => <Chip key={id} size="small" label={nomeFila(id)} />)
                  : <Chip size="small" variant="outlined" label="Todas as filas" />}
              </div>
              <div className={c.fraco}>{`por ${m.atualizado_por || m.criado_por || "-"}`}</div>
            </div>
          ))}
        </div>
      )}
      <Editor manual={editando} filas={filas} tags={tagsConhecidas} onClose={() => setEditando(null)} onSalvo={carregar} />
      {pilula && (
        <EditorPilula manual={pilula} filas={filas} tags={tagsConhecidas} onClose={() => setPilula(null)}
          onSalvo={(m) => { setPilula(m); carregar(); }} />
      )}
    </MainContainer>
  );
};

export default Manuais;
